import React from 'react'
import AntdCollapse from 'antd/es/collapse'
import type { CollapseProps } from 'antd/es/collapse'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Collapse as ShadcnSeamCollapse } from './Collapse.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/collapse`, put behind the design toggle
 * after the two POPUP seams (`../Select`, `../Dropdown`) and `../Menu`.
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this
 * component renders antd's own Collapse with the props it was given: same
 * element, same class names, same DOM. `src/antd` never SHADOWED Collapse —
 * the barrel handed back antd's own component — so the parity baseline here is
 * `antd/es/collapse` itself, as it is for Button, Space, Typography and Alert,
 * and NOT the `../Tag` / `../Divider` case.
 * `../__tests__/collapse-seam-parity.test.tsx` asserts that byte for byte
 * across a prop matrix drawn from what the 56 call sites actually pass.
 *
 * ---------------------------------------------------------------------------
 * `Collapse.Panel` IS ANTD'S OWN, IN BOTH MODES, AND HAS TO BE
 *
 * Ten call sites still use the legacy children API, and one of them reads the
 * static at module scope (`const { Panel } = Collapse` in
 * `src/new-components/api-card/Components/CardWrapper.tsx`) — so the static
 * cannot dispatch per render. It does not need to: antd's branch needs the
 * real `CollapsePanel`, because rc-collapse's `useItems` CLONES each child
 * with `isActive`/`onItemClick`/`prefixCls` and a marker component would
 * render none of it; the shadcn branch reads `element.props` off the children
 * array and never renders the child's own type at all, exactly as
 * rc-collapse's own `getNewChild` does. So antd's component serves both.
 * ------------------------------------------------------------------------- */
const CollapseDispatcher = (props: CollapseProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamCollapse {...props} />

  return <AntdCollapse {...props} />
}

CollapseDispatcher.displayName = 'Collapse'

export const Collapse = Object.assign(CollapseDispatcher, {
  Panel: AntdCollapse.Panel,
})
